import { Alert, Box, Button, Loader, SimpleGrid, Stack, Text } from '@mantine/core';
import { useMemo } from 'react';
import { type IntakeAnswers, type IntakeAction, getBookedProviderIds } from '../types';
import { ProviderCard } from '../ProviderCard';
import { useIntakeDataContext } from '../IntakeDataContext';

interface StepProvidersProps {
  answers: IntakeAnswers;
  dispatch: React.Dispatch<IntakeAction>;
  onWaitlist?: () => void;
  onDecline?: () => void;
}

export function StepProviders({
  answers,
  dispatch,
  onWaitlist,
  onDecline,
}: StepProvidersProps): JSX.Element {
  const { providers: allProviders, providersLoading, providersError, authError } =
    useIntakeDataContext();
  // Render the matching bot's results directly (already filtered by language + service-type +
  // referral capacity, capped to 3); no client-side re-scoring or fallback-to-full-pool.
  const providers = allProviders;
  const bookedProviderIds = useMemo(() => getBookedProviderIds(answers), [answers.bookings]);
  const selectableProviders = useMemo(
    () => providers.filter((p) => !bookedProviderIds.has(p.id)),
    [providers, bookedProviderIds],
  );
  const displayProviders = selectableProviders.slice(0, 3);

  if (providersLoading) {
    return (
      <Box ta="center" py="xl">
        <Loader size="md" color="gold" />
      </Box>
    );
  }

  if ((authError || providersError) && allProviders.length === 0) {
    return (
      <Alert color="red" title="Could not load providers">
        {authError ?? providersError}
      </Alert>
    );
  }

  if (displayProviders.length === 0) {
    const noReferralCapacity = allProviders.length === 0;
    // No selectable provider for this service — either none matched, or every match was already
    // booked for another service this round. Either way, let the client waitlist or skip so they
    // are never stranded with a disabled Next.
    if (onWaitlist && onDecline) {
      return (
        <Box className="intake-providers-fade-in">
          <Alert color="gold" title={noReferralCapacity ? 'No providers available' : 'No additional providers available'}>
            {noReferralCapacity
              ? 'No providers currently have open referral requests with available capacity for this service type.'
              : 'Each service must be scheduled with a different provider, and the remaining matches are already chosen for your other services.'}
            {' '}You can join our waitlist and we will contact you when a provider becomes available.
          </Alert>
          <Stack gap="sm" mt="md">
            <Button color="gold" onClick={onWaitlist}>
              Yes, put me on a wait list
            </Button>
            <Button variant="default" onClick={onDecline}>
              No, skip this service
            </Button>
          </Stack>
        </Box>
      );
    }
    return (
      <Box className="intake-providers-fade-in">
        <Alert
          color="gold"
          title={noReferralCapacity ? 'No providers available' : 'No additional providers available'}
        >
          {noReferralCapacity
            ? 'No providers currently have open referral requests with available capacity.'
            : 'Each service must be scheduled with a different provider, and there are no remaining matches for your preferences. Go back to adjust your choices, or contact support for help.'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box className="intake-providers-fade-in">
      <Text size="sm" c="dimmed" mb="md">
        Based on your preferences, here are providers who may be a good fit. Select one to continue.
        {bookedProviderIds.size > 0 ? (
          <>
            {' '}
            Providers you already chose for another service are not shown.
          </>
        ) : null}
      </Text>
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
        {displayProviders.map((p) => (
          <ProviderCard
            key={p.id}
            provider={p}
            avatarSize={125}
            selected={answers.selectedProviderId === p.id}
            onSelect={(id) => dispatch({ type: 'SET_SELECTED_PROVIDER_ID', payload: id })}
          />
        ))}
      </SimpleGrid>
    </Box>
  );
}
